import {
	designTokens,
	Flex,
	RiAlertFill,
	RiArrowRightSLine,
	RiCheckboxCircleFill,
	RiErrorWarningFill
} from "@itandi/itandi-bb-ui";
import Link from "next/link";
import Styles from "./StatusCard.module.scss";

type StatusCardVariant = "default" | "danger" | "warning" | "success";

type Props = {
	count: number;
	href: string;
	label: string;
	variant: StatusCardVariant;
};

const baseStyles = {
	"--arrow-size": "24px",
	"--background": designTokens["color/background/default/white"],
	"--border-radius": `${designTokens["border-radius/s"]}px`,
	"--font-size-count": `${designTokens["text/label/l"]}px`,
	"--font-size-description": `${designTokens["text/label/s"]}px`,
	"--font-size-label": `${designTokens["text/label/m"]}px`,
	"--gap": `${designTokens["spacing/xs"]}px`,
	"--gap-inner": `${designTokens["spacing/xxxs"]}px`,
	"--gap-text": `${designTokens["spacing/xxs"]}px`,
	"--icon-size": "20px",
	"--padding": `${designTokens["spacing/s"]}px`,
	"--shadow": designTokens["elevation/1"]
};

const variantStyles = {
	danger: {
		"--arrow-color": designTokens["color/text/danger"],
		"--border-left": `${designTokens["spacing/xxs"]}px solid ${designTokens["color/border/danger"]}`,
		"--count-color": designTokens["color/text/danger"],
		"--hover-background": designTokens["color/background/danger/subtle"],
		"--icon-color": designTokens["color/icon/danger"],
		"--label-color": designTokens["color/text/danger"]
	},
	default: {
		"--arrow-color": designTokens["color/text/subtle"],
		"--border-left": "none",
		"--count-color": designTokens["color/text/default"],
		"--hover-background": designTokens["color/background/info/subtle"],
		"--icon-color": designTokens["color/icon/subtle"],
		"--label-color": designTokens["color/text/subtle"]
	},
	success: {
		"--arrow-color": designTokens["color/text/success"],
		"--border-left": `${designTokens["spacing/xxs"]}px solid ${designTokens["color/border/success"]}`,
		"--count-color": designTokens["color/text/success"],
		"--hover-background": designTokens["color/background/success/subtle"],
		"--icon-color": designTokens["color/icon/success"],
		"--label-color": designTokens["color/text/success"]
	},
	warning: {
		"--arrow-color": designTokens["color/text/warning"],
		"--border-left": `${designTokens["spacing/xxs"]}px solid ${designTokens["color/border/warning"]}`,
		"--count-color": designTokens["color/text/warning"],
		"--hover-background": designTokens["color/background/warning/subtle"],
		"--icon-color": designTokens["color/icon/warning"],
		"--label-color": designTokens["color/text/warning"]
	}
} as const satisfies Record<StatusCardVariant, Record<string, string | number>>;

const getCardCssVariables = (variant: StatusCardVariant) => ({
	...baseStyles,
	...variantStyles[variant]
});

export function StatusCard({ label, href, count, variant }: Props) {
	const customStyles = getCardCssVariables(variant);
	const iconVariant = {
		danger: RiErrorWarningFill,
		default: null,
		success: RiCheckboxCircleFill,
		warning: RiAlertFill
	} as const satisfies Record<
		StatusCardVariant,
		React.ComponentType<{ className?: string }> | null
	>;
	const Icon = iconVariant[variant];

	return (
		<Flex
			czz={{
				...customStyles,
				"@media (max-width: 480px)": {
					flex: "1 1 100%",
					maxWidth: "100%",
					width: "100%"
				},
				flex: "0 0 224px"
			}}
		>
			<Link className={Styles.Container} href={href}>
				<Flex
					alignItems="flex-start"
					czz={{ flex: "1 0 0" }}
					gap={Icon ? designTokens["spacing/xxxs"] : undefined}
				>
					{Icon ? <Icon className={Styles.Icon} /> : null}
					<Flex czz={{ flex: "1 0 0" }} gap={designTokens["spacing/xxs"]}>
						<Flex gap={designTokens["spacing/xs"]} width="100%">
							<Flex alignItems="center" czz={{ flex: "1 0 0" }}>
								<span className={Styles.Label}>{label}</span>
							</Flex>
							<Flex alignItems="center" czz={{ alignSelf: "stretch" }}>
								<span className={Styles.Count}>{count}</span>
							</Flex>
						</Flex>
					</Flex>
				</Flex>
				<RiArrowRightSLine className={Styles.Arrow} size={24} />
			</Link>
		</Flex>
	);
}
